import React, { useState, useEffect, useMemo, useRef } from 'react';
import { format } from 'date-fns';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { createChart, ColorType, CrosshairMode, LineStyle } from 'lightweight-charts';
import {
  PRECIO_OFICIAL, PALETTE, SPANISH_MONTHS_SHORT,
  fmtCurrency, fmtDollars, fmtPct,
  fmtDateLong, isoOnly, buildPriceInsight, aggregateDaily,
  formatRelativeTime, freshnessLevel, summarizeFreshness,
} from './chartShared';
import './WeeklyAverageYearChart.css';
import './PriceRangeChart.css';

const REFRESH_MS = 5 * 60 * 1000;
const ZONE_COLOR = { norte: PALETTE.green, sur: PALETTE.blue };

const fetchZoneWeekly = async (zone, token) => {
  const res = await axios.get(
    `${process.env.REACT_APP_API_URL}/prices/weekly-summary/?zone=${zone}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

const WeeklySummary = () => {
  const { t } = useTranslation();
  const [zone, setZone] = useState('norte'); // 'norte' | 'sur' | 'both'
  const [dataByZone, setDataByZone] = useState({ norte: [], sur: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedDate, setExpandedDate] = useState(null);
  const [, setNowTick] = useState(0);

  const chartContainerRef = useRef(null);
  const tooltipRef        = useRef(null);

  const isBoth = zone === 'both';

  useEffect(() => {
    let cancelled = false;
    const run = async (silent = false) => {
      const token = localStorage.getItem('access_token');
      if (!token) { setError(t('auth_required')); setLoading(false); return; }
      if (!silent) setLoading(true);
      try {
        const zonesToFetch = isBoth ? ['norte', 'sur'] : [zone];
        const results = await Promise.all(zonesToFetch.map(z => fetchZoneWeekly(z, token)));
        if (cancelled) return;
        const next = { norte: [], sur: [] };
        zonesToFetch.forEach((z, i) => { next[z] = results[i]; });
        setDataByZone(next);
        setError(null);
      } catch (err) {
        console.error('Error fetching weekly data:', err);
        if (!silent) setError(t('weekly_error'));
      } finally {
        if (!silent && !cancelled) setLoading(false);
      }
    };
    run();
    const interval = setInterval(() => run(true), REFRESH_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [t, zone, isBoth]);

  useEffect(() => {
    const id = setInterval(() => setNowTick(n => n + 1), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const seriesNorte = useMemo(() => aggregateDaily(dataByZone.norte), [dataByZone.norte]);
  const seriesSur   = useMemo(() => aggregateDaily(dataByZone.sur),   [dataByZone.sur]);
  const series      = isBoth ? seriesNorte : (zone === 'sur' ? seriesSur : seriesNorte);
  const weeklyData  = isBoth ? [...dataByZone.norte, ...dataByZone.sur] : (zone === 'sur' ? dataByZone.sur : dataByZone.norte);

  // Group raw reports by ISO date for drill-down (single-zone only)
  const reportsByDate = useMemo(() => {
    const map = new Map();
    const source = zone === 'sur' ? dataByZone.sur : dataByZone.norte;
    for (const r of source) {
      const iso = isoOnly(r.date);
      if (!map.has(iso)) map.set(iso, []);
      map.get(iso).push(r);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => new Date(a.date) - new Date(b.date));
    }
    return map;
  }, [dataByZone, zone]);

  const freshness = useMemo(() => summarizeFreshness(weeklyData), [weeklyData]);

  const stats = useMemo(() => {
    if (!series.length) return null;
    const today = series[series.length - 1];
    const prev  = series.length > 1 ? series[series.length - 2] : null;
    const diff  = prev ? today.value - prev.value : 0;
    const avg   = series.reduce((s, p) => s + p.value, 0) / series.length;
    const minPt = series.reduce((a, b) => (b.value < a.value ? b : a));
    const maxPt = series.reduce((a, b) => (b.value > a.value ? b : a));
    const diffOficial    = avg - PRECIO_OFICIAL;
    const diffOficialPct = (diffOficial / PRECIO_OFICIAL) * 100;
    return { today, prev, diff, avg, minPt, maxPt, diffOficial, diffOficialPct };
  }, [series]);

  const dualStats = useMemo(() => {
    if (!isBoth) return null;
    const lastN = seriesNorte[seriesNorte.length - 1];
    const lastS = seriesSur[seriesSur.length - 1];
    const avgN = seriesNorte.length ? seriesNorte.reduce((a, b) => a + b.value, 0) / seriesNorte.length : null;
    const avgS = seriesSur.length   ? seriesSur.reduce((a, b) => a + b.value, 0) / seriesSur.length   : null;
    const spread = (lastN && lastS) ? lastN.value - lastS.value : null;
    return { norte: lastN, sur: lastS, avgN, avgS, spread };
  }, [isBoth, seriesNorte, seriesSur]);

  const insight = useMemo(() => {
    if (isBoth) {
      if (!dualStats || dualStats.spread == null) return null;
      const abs = Math.abs(dualStats.spread);
      if (abs < 0.05) return { icon: '⚖️', text: 'Ambas zonas casi iguales esta semana.' };
      const higher = dualStats.spread > 0 ? 'Norte' : 'Sur';
      return { icon: '🌱', text: `${higher} pagó ${fmtDollars(abs)} más en promedio que ${dualStats.spread > 0 ? 'Sur' : 'Norte'} esta semana.` };
    }
    return buildPriceInsight(series);
  }, [isBoth, dualStats, series]);

  // Chart lifecycle ─────────────────────────────────────────
  useEffect(() => {
    if (!chartContainerRef.current) return undefined;
    const hasN = seriesNorte.length > 0;
    const hasS = seriesSur.length > 0;
    if (!isBoth && !series.length) return undefined;
    if (isBoth && !hasN && !hasS) return undefined;

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor:  PALETTE.lightGray,
        fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        attributionLogo: false,
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { color: '#f0f1f5' },
      },
      rightPriceScale: { borderVisible: false, scaleMargins: { top: 0.18, bottom: 0.15 } },
      timeScale: {
        borderVisible: false,
        timeVisible: false,
        tickMarkFormatter: (time) => {
          if (time && typeof time === 'object' && 'year' in time) {
            const m = SPANISH_MONTHS_SHORT[time.month - 1] ?? '';
            return `${time.day} ${m}`;
          }
          return '';
        },
      },
      crosshair: {
        mode: CrosshairMode.Magnet,
        vertLine: { color: PALETTE.lightGray, width: 1, style: LineStyle.Dotted, labelVisible: false },
        horzLine: { color: PALETTE.lightGray, width: 1, style: LineStyle.Dotted, labelVisible: false },
      },
      handleScroll: false,
      handleScale:  false,
    });

    const makeAreaSeries = (color, alpha) => chart.addAreaSeries({
      lineColor:    color,
      lineWidth:    3,
      topColor:     `${color}${alpha}`,
      bottomColor:  `${color}00`,
      priceLineVisible: false,
      lastValueVisible: false,
      priceFormat:  { type: 'price', precision: 2, minMove: 0.01 },
    });

    const seriesMap = [];
    if (isBoth) {
      if (hasN) {
        const s = makeAreaSeries(ZONE_COLOR.norte, '52');
        s.setData(seriesNorte.map(({ time, value }) => ({ time, value })));
        seriesMap.push({ key: 'norte', label: 'Norte', color: ZONE_COLOR.norte, lwSeries: s });
      }
      if (hasS) {
        const s = makeAreaSeries(ZONE_COLOR.sur, '40');
        s.setData(seriesSur.map(({ time, value }) => ({ time, value })));
        seriesMap.push({ key: 'sur', label: 'Sur', color: ZONE_COLOR.sur, lwSeries: s });
      }
    } else {
      const color = ZONE_COLOR[zone] || ZONE_COLOR.norte;
      const s = makeAreaSeries(color, '52');
      s.setData(series.map(({ time, value }) => ({ time, value })));
      seriesMap.push({ key: zone, label: zone === 'sur' ? 'Sur' : 'Norte', color, lwSeries: s });
    }

    if (seriesMap[0]) {
      seriesMap[0].lwSeries.createPriceLine({
        price: PRECIO_OFICIAL,
        color: PALETTE.lightGray,
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: true,
        title: 'Oficial',
      });
    }

    if (!isBoth && stats && seriesMap[0]) {
      seriesMap[0].lwSeries.setMarkers([
        { time: stats.maxPt.time, position: 'aboveBar', color: PALETTE.green, shape: 'arrowDown', text: `Máx ${fmtCurrency(stats.maxPt.value)}` },
        { time: stats.minPt.time, position: 'belowBar', color: PALETTE.red,   shape: 'arrowUp',   text: `Mín ${fmtCurrency(stats.minPt.value)}` },
      ]);
    }

    chart.timeScale().fitContent();

    chart.subscribeCrosshairMove(param => {
      const tip = tooltipRef.current;
      const container = chartContainerRef.current;
      if (!tip || !container) return;
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (!param.time || !param.point || param.point.x < 0 || param.point.y < 0 || param.point.x > w || param.point.y > h) {
        tip.style.display = 'none';
        return;
      }
      const probeData = seriesMap.map(s => param.seriesData.get(s.lwSeries)).find(Boolean);
      if (!probeData) { tip.style.display = 'none'; return; }
      const tm = probeData.time;
      const date = (tm && typeof tm === 'object' && 'year' in tm)
        ? new Date(tm.year, tm.month - 1, tm.day)
        : new Date(tm * 1000);
      if (Number.isNaN(date.getTime())) { tip.style.display = 'none'; return; }
      const dayLabel = fmtDateLong(date);

      let body = '';
      if (isBoth) {
        body = seriesMap.map(s => {
          const d = param.seriesData.get(s.lwSeries);
          if (!d) return null;
          const diff = d.value - PRECIO_OFICIAL;
          const diffColor = diff >= 0 ? PALETTE.green : PALETTE.red;
          return `<div class="prc-tip__row">
            <span class="prc-tip__zone-dot" style="background:${s.color}"></span>
            <span class="prc-tip__zone">${s.label}</span>
            <span class="prc-tip__zone-val">${fmtCurrency(d.value)}</span>
            <span class="prc-tip__zone-diff" style="color:${diffColor}">${diff >= 0 ? '+' : ''}${fmtDollars(diff)}</span>
          </div>`;
        }).filter(Boolean).join('');
      } else {
        const d = param.seriesData.get(seriesMap[0].lwSeries);
        if (!d) { tip.style.display = 'none'; return; }
        const diff = d.value - PRECIO_OFICIAL;
        const diffLabel = diff >= 0
          ? `<span style="color:${PALETTE.green}">${fmtDollars(diff)} sobre el oficial</span>`
          : `<span style="color:${PALETTE.red}">${fmtDollars(diff)} bajo el oficial</span>`;
        body = `
          <div class="prc-tip__price">${fmtCurrency(d.value)}</div>
          <div class="prc-tip__diff">${diffLabel}</div>
        `;
      }
      tip.innerHTML = `<div class="prc-tip__date">${dayLabel}</div>${body}`;
      tip.style.display = 'block';
      const tipW = tip.clientWidth;
      const tipH = tip.clientHeight;
      let x = param.point.x + 16;
      let y = param.point.y - tipH - 16;
      if (x + tipW > w) x = param.point.x - tipW - 16;
      if (y < 0) y = param.point.y + 16;
      tip.style.left = `${x}px`;
      tip.style.top  = `${y}px`;
    });

    const handleResize = () => {
      if (!chartContainerRef.current) return;
      chart.applyOptions({ width: chartContainerRef.current.clientWidth });
      chart.timeScale().fitContent();
    };
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, [series, stats, isBoth, seriesNorte, seriesSur, zone]);

  if (loading) return <div className="loading">{t('loading_weekly')}</div>;
  if (error)   return <div className="error">{error}</div>;

  const hasData = isBoth ? (seriesNorte.length > 0 || seriesSur.length > 0) : series.length > 0;
  const freshLevel = freshnessLevel(freshness.latest);

  return (
    <div className="weekly-summary-container">
      <div className="zone-selector-row">
        <h2>{t('weekly_summary')}</h2>
        <div className="zone-tabs">
          <button className={`zone-tab ${zone === 'norte' ? 'zone-tab--active' : ''}`} onClick={() => setZone('norte')}>{t('zona_norte')}</button>
          <button className={`zone-tab ${zone === 'sur'   ? 'zone-tab--active' : ''}`} onClick={() => setZone('sur')}>{t('zona_sur')}</button>
          <button className={`zone-tab ${isBoth ? 'zone-tab--active' : ''}`} onClick={() => setZone('both')}>Ambas</button>
        </div>
      </div>

      <div className="prc-card">
        {/* Freshness bar */}
        {freshness.latest && (
          <div className="prc-freshness">
            <span className={`prc-freshness__dot prc-freshness__dot--${freshLevel}`} />
            <span>
              <span className="prc-freshness__strong">Última actualización:</span> {formatRelativeTime(freshness.latest)}
              {freshness.todayCount > 0 && (
                <> · {freshness.todayCount} {freshness.todayCount === 1 ? 'reporte' : 'reportes'} hoy</>
              )}
            </span>
          </div>
        )}

        {/* Hero — single zone */}
        {!isBoth && hasData && stats && (
          <div className="prc-hero">
            <div className="prc-hero__main">
              <div className="prc-hero__value">{fmtCurrency(stats.avg)}</div>
              <div className="prc-hero__caption">promedio de la semana</div>
            </div>
            <div className="prc-hero__deltas">
              <span className="prc-delta">
                Última caja: <strong style={{ color: PALETTE.navy }}>{fmtCurrency(stats.today.value)}</strong>
              </span>
              <span className={`prc-delta ${stats.diffOficial >= 0 ? 'prc-delta--up' : 'prc-delta--down'}`}>
                {stats.diffOficial >= 0
                  ? `${fmtPct(stats.diffOficialPct)} sobre el precio oficial 🌱`
                  : `${fmtPct(stats.diffOficialPct)} bajo el precio oficial ⚠️`}
              </span>
            </div>
          </div>
        )}

        {/* Hero — dual zone */}
        {isBoth && dualStats && (
          <>
            <div className="prc-hero--dual">
              <div className="prc-hero__zone prc-hero__zone--norte">
                <div className="prc-hero__zone-label">Promedio Norte</div>
                <div className="prc-hero__zone-value">
                  {dualStats.avgN != null ? fmtCurrency(dualStats.avgN) : '—'}
                </div>
              </div>
              <div className="prc-hero__zone prc-hero__zone--sur">
                <div className="prc-hero__zone-label">Promedio Sur</div>
                <div className="prc-hero__zone-value">
                  {dualStats.avgS != null ? fmtCurrency(dualStats.avgS) : '—'}
                </div>
              </div>
            </div>
            {dualStats.spread != null && (
              <div className="prc-hero__spread">
                Última caja Norte <strong>{dualStats.norte ? fmtCurrency(dualStats.norte.value) : '—'}</strong> · Sur <strong>{dualStats.sur ? fmtCurrency(dualStats.sur.value) : '—'}</strong>
              </div>
            )}
          </>
        )}

        <div className="prc-chart-wrap">
          <div className="prc-chart" ref={chartContainerRef} />
          <div className="prc-tip" ref={tooltipRef} />
          {!hasData && <div className="prc-empty">Aún no hay precios para esta semana.</div>}
        </div>

        {/* Legend (when comparing) */}
        {isBoth && hasData && (
          <div className="prc-chart-legend">
            <span className="prc-chart-legend__item">
              <span className="prc-chart-legend__swatch" style={{ background: ZONE_COLOR.norte }} />
              Zona Norte
            </span>
            <span className="prc-chart-legend__item">
              <span className="prc-chart-legend__swatch" style={{ background: ZONE_COLOR.sur }} />
              Zona Sur
            </span>
          </div>
        )}

        {insight && (
          <div className="prc-insight">
            <span className="prc-insight__icon">{insight.icon}</span>
            <span className="prc-insight__text">{insight.text}</span>
          </div>
        )}
      </div>

      {/* Day-by-day table with drill-down (single zone only) */}
      {!isBoth && hasData && (
        <div className="prc-card">
          <h3 className="prc-section__title">Día por día</h3>
          <p className="prc-table__hint">Tocá una fila para ver los reportes individuales del día.</p>
          <table className="prc-table prc-table--clickable">
            <thead>
              <tr>
                <th />
                <th>Fecha</th>
                <th className="prc-table__right">Precio / Caja</th>
                <th className="prc-table__right">Precio / Kg</th>
                <th className="prc-table__right">Variación</th>
                <th className="prc-table__right">Reportes</th>
              </tr>
            </thead>
            <tbody>
              {series.map((day, i) => {
                const previous = i > 0 ? series[i - 1].value : day.value;
                const diff = day.value - previous;
                const pct = previous !== 0 ? (diff / previous * 100) : 0;
                const expanded = expandedDate === day.iso;
                const reports = reportsByDate.get(day.iso) ?? [];
                const dayDate = new Date(day.time.year, day.time.month - 1, day.time.day);
                const avgKg = reports.length
                  ? reports.reduce((s, r) => s + parseFloat(r.price_per_kg || 0), 0) / reports.length
                  : 0;
                return (
                  <React.Fragment key={day.iso}>
                    <tr
                      className={expanded ? 'prc-table__row--expanded' : ''}
                      onClick={() => setExpandedDate(expanded ? null : day.iso)}
                    >
                      <td className="prc-table__caret">{expanded ? '▾' : '▸'}</td>
                      <td>{format(dayDate, 'dd MMM yyyy')}</td>
                      <td className="prc-table__right prc-table__strong">{fmtCurrency(day.value)}</td>
                      <td className="prc-table__right prc-table__muted">{fmtCurrency(avgKg, 3)}</td>
                      <td className={`prc-table__right ${i === 0 ? '' : (diff >= 0 ? 'prc-table__up' : 'prc-table__down')}`}>
                        {i === 0 ? '—' : `${diff >= 0 ? '▲' : '▼'} ${Math.abs(pct).toFixed(1)}%`}
                      </td>
                      <td className="prc-table__right">
                        <span className="prc-badge">{day.count}</span>
                      </td>
                    </tr>
                    {expanded && reports.length > 0 && (
                      <tr className="prc-table__detail-row">
                        <td colSpan={6}>
                          <div className="prc-detail">
                            <div className="prc-detail__head">
                              {reports.length} {reports.length === 1 ? 'reporte' : 'reportes'} el {fmtDateLong(dayDate)}
                            </div>
                            <table className="prc-detail__table">
                              <thead>
                                <tr>
                                  <th>Hora</th>
                                  <th className="prc-table__right">Caja</th>
                                  <th className="prc-table__right">Kg</th>
                                  <th className="prc-table__right">19.5</th>
                                  <th className="prc-table__right">18.8</th>
                                  <th className="prc-table__right">vs Oficial</th>
                                </tr>
                              </thead>
                              <tbody>
                                {reports.map((r, ri) => {
                                  const pBox = parseFloat(r.price_per_box || 0);
                                  const dOfic = pBox - PRECIO_OFICIAL;
                                  const time = new Date(r.date);
                                  const timeStr = Number.isNaN(time.getTime())
                                    ? '—'
                                    : time.toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' });
                                  return (
                                    <tr key={ri}>
                                      <td className="prc-table__muted">{timeStr}</td>
                                      <td className="prc-table__right prc-table__strong">{fmtCurrency(pBox)}</td>
                                      <td className="prc-table__right">{fmtCurrency(parseFloat(r.price_per_kg || 0), 3)}</td>
                                      <td className="prc-table__right prc-table__muted">{r.price_per_kg_19_5 ? fmtCurrency(parseFloat(r.price_per_kg_19_5), 4) : '—'}</td>
                                      <td className="prc-table__right prc-table__muted">{r.price_per_kg_18_8 ? fmtCurrency(parseFloat(r.price_per_kg_18_8), 4) : '—'}</td>
                                      <td className={`prc-table__right ${dOfic >= 0 ? 'prc-table__up' : 'prc-table__down'}`}>
                                        {dOfic >= 0 ? '▲' : '▼'} {fmtDollars(dOfic)}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default WeeklySummary;
